import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'
import TiptapEditor from '../../../components/common/TiptapEditor'
import { parsePageToKioskSections } from '../../../utils/PageToKioskContent'

export default function AdminPageEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = !!id && id !== 'new'

  const RESERVED_PAGE_IDS = new Set([1, 2, 3])  // 인삿말, 마루빛소개, 오시는길 page_id
  const isReserved = isEdit && RESERVED_PAGE_IDS.has(Number(id))

  const [slug, setSlug] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [isVisible, setIsVisible] = useState(1)
  const [sortOrder, setSortOrder] = useState(0)
  const [loading, setLoading] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (isEdit) fetchPage()
  }, [id])

  const fetchPage = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/page/admin/${id}`))

      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setSlug(data.slug)
      setTitle(data.title)
      setBody(data.body ?? '')
      setIsVisible(data.is_visible)
      setSortOrder(data.sort_order)
    } catch {
      alert('페이지 정보를 불러오는 데 실패했습니다.')
      navigate('/admin/page')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!slug.trim()) { alert('슬러그를 입력해주세요.'); return }
    if (!title.trim()) { alert('제목을 입력해주세요.'); return }

    setSubmitting(true)
    try {
        const payload = { slug, title, body: body || null, is_visible: isVisible, sort_order: sortOrder }

        const res = await fetchWithAuth(
        getApiUrl(isEdit ? `/marubit/api/page/admin/${id}` : '/marubit/api/page/admin'),
        {
            method: isEdit ? 'PUT' : 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        }
        )
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail ?? '저장 실패')
      }
      alert(isEdit ? '수정되었습니다.' : '페이지가 생성되었습니다.')
      navigate('/admin/page')
    } catch (e: any) {
      alert(e.message ?? '저장에 실패했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1250px] mx-auto">

        <div className="mb-8">
          <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">
            {isEdit ? '페이지 수정' : '페이지 추가'}
          </h1>
          <p className="text-[13px] text-gray-400">
            {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
          </p>
        </div>

        {/* 슬러그 */}
        <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-1">슬러그</h2>
          <p className="text-[12px] text-gray-400 ml-[13px] mb-4">URL 경로로 사용됩니다. 예: greeting → /page/greeting</p>
          <input
            type="text"
            placeholder="영문 소문자, 숫자, 하이픈만 사용 *"
            value={slug}
            onChange={e => !isReserved && setSlug(e.target.value)}
            disabled={isReserved}
            className={[
              "w-full px-4 py-3 text-[14px] border-[1.5px] rounded-[10px] outline-none transition-colors font-mono",
              isReserved
                ? "border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed"
                : "border-gray-200 focus:border-[#887267]"
            ].join(" ")}
          />
          {isReserved && (
            <p className="text-[12px] text-orange-400 ml-[13px] mt-2">
              바로가기 메뉴에 연결된 페이지의 슬러그는 변경할 수 없습니다.
            </p>
          )}
        </div>

        {/* 제목 */}
        <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">제목</h2>
          <input
            type="text"
            placeholder="페이지 제목을 입력해주세요 *"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
          />
        </div>

        {/* 본문 */}
        <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">본문</h2>
          <TiptapEditor
            onChange={setBody}
            initialContent={body}
            imageUploadUrl="/marubit/api/page/admin/upload-image"
          />
        </div>

        {/* 정렬 순서 */}
        <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">정렬 순서</h2>
          <input
            type="number"
            min={0}
            value={sortOrder}
            onChange={e => setSortOrder(Number(e.target.value))}
            className="w-[120px] px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
          />
        </div>

        {/* 노출 여부 */}
        <div className="bg-white rounded-2xl p-7 mb-6 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">노출 여부</h2>
          <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-[#f5f2f0] rounded-[10px] border border-[#e8e0dc] w-fit">
            <input
              type="checkbox"
              checked={isVisible === 1}
              onChange={e => setIsVisible(e.target.checked ? 1 : 0)}
              className="w-4 h-4 cursor-pointer accent-[#887267]"
            />
            <span className="text-[13px] font-semibold text-[#887267]">노출</span>
          </label>
        </div>

        {/* 버튼 */}
        <div className="flex gap-3">
          {isEdit && (
            <button
              onClick={() => {
                const { sections, firstImageUrl } = parsePageToKioskSections(body);
                navigate(`/admin/kiosk/content/register?kiosk_code=${localStorage.getItem("kiosk_code")}`, {
                  state: { title, sections, firstImageUrl }
                });
              }}
              className="flex-1 h-[52px] bg-white text-[#887267] border-[1.5px] border-[#887267] rounded-xl text-[16px] font-bold cursor-pointer hover:bg-[#f5f2f0] transition-colors"
            >
              키오스크 등록
            </button>
          )}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={[
              'flex-1 h-[52px] text-white border-none rounded-xl text-[16px] font-bold transition-colors shadow-[0_4px_16px_rgba(136,114,103,0.25)]',
              submitting ? 'bg-[#b8a39b] cursor-not-allowed' : 'bg-[#887267] cursor-pointer hover:bg-[#6b5248]',
            ].join(' ')}
          >
            {submitting ? '저장 중...' : '저장'}
          </button>
          <button
            onClick={() => navigate('/admin/page')}
            className="flex-1 h-[52px] bg-white text-[#887267] border-[1.5px] border-[#887267] rounded-xl text-[16px] font-bold cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >
            취소
          </button>
        </div>
      </div>
    </div>
  )
}